import { GOAL_TABS_MAX_VISIBLE } from "@repo/shared";
import { Trash2, TriangleAlert } from "lucide-react";
import { TabsTrigger } from "@/components/ui/tabs";
import { GoalDropdown } from "./goal-dropdown";

type GoalTabTriggersProps = {
  goals: Array<{ id: string; name: string }>;
  currentGoalId: string | null;
  onSelectGoal: (goalId: string) => void;
  onDeleteGoal: (goalId: string) => void;
  canDelete: boolean;
  missingGoalIds: Set<string>;
  showValidationErrors?: boolean;
  /** Label prefix for the per-goal delete control ("Delete <goal>"). */
  deleteLabel: string;
};

/**
 * The goal entries of the L1 tab bar (advisor and public alike). Up to
 * {@link GOAL_TABS_MAX_VISIBLE} goals render as one tab each, exactly as before QL-593; past that
 * they collapse into a single {@link GoalDropdown}. Must sit inside a `TabsList` whose tab values
 * use the `goal:<id>` scheme.
 */
export function GoalTabTriggers({
  goals,
  currentGoalId,
  onSelectGoal,
  onDeleteGoal,
  canDelete,
  missingGoalIds,
  showValidationErrors,
  deleteLabel,
}: GoalTabTriggersProps) {
  if (goals.length > GOAL_TABS_MAX_VISIBLE) {
    return (
      <GoalDropdown
        goals={goals}
        currentGoalId={currentGoalId}
        onSelectGoal={onSelectGoal}
        onDeleteGoal={onDeleteGoal}
        canDelete={canDelete}
        missingGoalIds={missingGoalIds}
        showValidationErrors={showValidationErrors}
      />
    );
  }

  return goals.map((goal) => (
    <TabsTrigger
      key={goal.id}
      value={`goal:${goal.id}`}
      className="shrink-0 flex items-center gap-2"
    >
      <span className="truncate max-w-[12rem]">{goal.name}</span>
      {showValidationErrors && missingGoalIds.has(goal.id) && (
        <TriangleAlert className="h-3.5 w-3.5 text-destructive shrink-0" />
      )}
      {canDelete && (
        // biome-ignore lint/a11y/useSemanticElements: nested inside a TabsTrigger button
        <span
          role="button"
          className="inline-flex h-5 w-5 items-center justify-center rounded-sm hover:bg-muted cursor-pointer transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onDeleteGoal(goal.id);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.stopPropagation();
              e.preventDefault();
              onDeleteGoal(goal.id);
            }
          }}
          aria-label={`${deleteLabel} ${goal.name}`}
          tabIndex={-1}
        >
          <Trash2 className="h-3 w-3" />
        </span>
      )}
    </TabsTrigger>
  ));
}
